import React, { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ProjectBanner from "../components/ProjectBanner";
import OverviewSection from "../components/sections/OverviewSection";
import DrawsSection from "../components/sections/DrawsSection";
import WaiversSection from "../components/sections/WaiversSection";
import ChangeOrdersSection from "../components/sections/ChangeOrdersSection";
import {
  CO_STATUS,
  normalizeChangeOrderStatus,
  isCoApproved,
} from "../../ChangeOrder/constants/changeOrderStatus";
import PreconSection from "../components/sections/PreconSection";
import BudgetSection from "../components/sections/BudgetSection";
import AiaBillingSection from "../components/sections/AiaBillingSection";
import DocumentsSection from "../components/sections/DocumentsSection";
import ConversationPanel from "../../../../Components/Common/ConversationPanel/ConversationPanel";
import { useToast } from "../../../../Components/Common/Toast/ToastContext";
import { useGcLayout } from "../../GcLayout";
import Sidebar from "../../../../Components/Sidebar/Sidebar";
import OverviewIcon from "../../../../Assets/Img/OverviewIcon";
import DrawRequestIcon from "../../../../Assets/Img/DrawRequestIcon";
import LineWaiverIcon from "../../../../Assets/Img/LineWaiverIcon";
import ChangeOrderIcon from "../../../../Assets/Img/ChangeOrderIcon";
import PreConIcon from "../../../../Assets/Img/PreConIcon";
import BudgetIcon from "../../../../Assets/Img/BudgetIcon";
import AiaBillingIcon from "../../../../Assets/Img/AiaBillingIcon";
import DocIcon from "../../../../Assets/Img/DocIcon";
import MessageIcon from "../../../../Assets/Img/MessageIcon";
import SOVSetup from "../components/sections/SOVSetup";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import NoDocuments from "Assets/Img/NoDocuments";
import {
  getProject,
  getGcProjectOverview,
  getGcPreconReview,
  getGcScheduleBudget,
  uploadChecklistDocument,
} from "../../../../services/projectService";
import { getLenderPreconReview } from "services/lenderService";
import BankPreconSection from "Pages/Bank/BankDetails/PreconSection";
import { uploadFileToS3 } from "../../GcPreconCommercial/UploadFileToS3";
import { getPreconConfig } from "../../GcPreconCommercial/preconConfig";
import { getSovVersions } from "services";
import {
  getGcLienItems,
  inviteLienWaiver,
} from "../../../../services/drawService";
import { getGcChangeOrders } from "../../../../services/changeOrderService";
import GcProjectDetailSkeleton from "../../../../Components/Common/Skeleton/GcProjectDetailSkeleton";
import UpdateProgress from "../components/sections/UpdateProgress";
import { capitalizeFirst } from "utils/Common/String";
import NetworkError from "Components/Common/NetworkError/NetworkError";
import { formatDate } from "utils/Common/Date";
import { formatStatus } from "../components/DrawHistoryTable";
import { STATUS_LABELS } from "Pages/GcPrecon/GcDashboard/GcDashboard";
import ScheduleOfValuesIcon from "Assets/Img/ScheduleOfValuesIcon";
import PreConstructionReportIcon from "Assets/Img/PreConstructionReportIcon";
import PreconChecklistIcon from "Assets/Img/PreconChecklistIcon";
import UpdateProgressIcon from "Assets/Img/UpdateProgressIcon";

// ── Shared value types ────────────────────────────────────────────────────────

interface CurrencyAmount {
  amount: number;
  currency: { symbol: string };
}

interface ProjectStatus {
  label: string;
  variant: string;
}

// ── API response shapes ───────────────────────────────────────────────────────

// TODO: Confirm backend response shape
interface ProjectData {
  projectName?: string;
  projectAddress?: string;
  lender?: {
    companyName?: string;
    firstName?: string;
    lastName?: string;
  };
  projectType?: string;
  projectStage?: string;
  contractValue?: CurrencyAmount;
  status?: string;
  statusVariant?: string;
  progressPercentage?: number;
  cscCompanyName?: string;
  adminCompanyName?: string;
  stakeholders?: {
    lenderBranch?: { companyName?: string };
  };
}
// TODO: Confirm backend response shape
interface SovVersion {
  id: string;
  versionNumber: number;
  status: string;
}

// TODO: Confirm backend response shape
interface RawChangeOrder {
  id?: string;
  number?: string;
  coNumber?: string;
  status?: string;
  totalAmount?: { amount?: number; currency?: { symbol?: string } };
  scheduleImpact?: number | string | null;
  title?: string;
  publishedAt?: string;
  submittedAt?: string;
  createdAt?: string;
}

// TODO: Confirm backend response shape
interface ChangeOrderAddOns {
  approvedCoSum?: {
    amount?: { amount?: number; currency?: { symbol?: string } };
  };
  revisedContractValue?: { amount?: number };
  pendingCoSum?: { amount?: { amount?: number } };
}

interface OverviewAmountField {
  amount?: number;
  currency?: { symbol?: string };
}

interface OverviewStakeholderPerson {
  firstName?: string;
  lastName?: string;
  companyName?: string;
  email?: string;
  phone?: string;
  address?: string;
}

interface OverviewApiData {
  name?: string;
  contractAmount?: OverviewAmountField;
  updatedContractValue?: OverviewAmountField;
  billedToDate?: OverviewAmountField;
  balanceToBill?: OverviewAmountField;
  estCompletionDate?: string;
  status?: string;
  drawRequest?: {
    drawNumber?: number;
    status?: string;
    requestedAmount?: OverviewAmountField;
    inspectionDate?: string;
  };
  recentActivities?: {
    activities: Array<{
      type: string;
      title: string;
      description: string | null;
      date: string;
      performedBy: string;
    }>;
    inspectionCount: number;
  };
  progressReport?: {
    inspectionDate?: string;
    inspectionSnapshot?: {
      lineItems?: Array<{
        aiaItem?: { itemName?: string };
        gcBilledPct?: number;
        isFlagged?: boolean;
        isIncludedFlagInReport?: boolean;
        note?: Record<string, string>;
      }>;
      phases?: Array<{
        phase: string;
        phaseOrder: number;
        phaseWeight: number;
        phaseCscEstimation: number;
        items: Array<{
          id: string;
          itemLabel: string;
          itemCode: string;
          itemWeight: number;
          cscEstimation: number;
          isFlagged: boolean;
          isIncludedFlagInReport: boolean;
          note?: Record<string, string>;
        }>;
      }>;
    };
  };
  stakeholders?: {
    owner?: OverviewStakeholderPerson;
    lender?: OverviewStakeholderPerson;
    inspectors?: OverviewStakeholderPerson[];
    architect?: OverviewStakeholderPerson;
  };
}

// TODO: Confirm backend response shape
interface LienWaiver {
  id?: string;
  signedAt?: string;
  inviteCount?: number;
  invitedAt?: string;
}

// TODO: Confirm backend response shape
interface LienItem {
  id?: string;
  subContractorName?: string;
  totalAmount: CurrencyAmount;
  lienWaiver?: LienWaiver;
}

interface DrawLienData {
  lienItems: LienItem[];
}

type LienItemsData = Record<string, DrawLienData>;

// ── Mapped / display types ────────────────────────────────────────────────────

interface ChangeOrderRow {
  id: string;
  description: string;
  submitted: string;
  amount: string;
  amountClass: "coAmtPos" | "coAmtNeg";
  schedule: string;
  status: string;
  statusVariant: string;
  isPrimary: boolean;
}

interface CoSummaryDisplay {
  approved: string;
  pending: string;
  revised: string;
}

interface ChangeSummaryProps extends CoSummaryDisplay {
  onSubmit: () => void;
}

interface WaiverAction {
  label: string;
  variant: string;
}

interface WaiverRow {
  vendor?: string;
  amount: string;
  sent: string;
  status: string;
  statusClass: string;
  drawId?: string;
  lienWaiverId?: string;
  sending?: boolean;
  action?: WaiverAction;
}

// ── UI data types ─────────────────────────────────────────────────────────────

interface ProjectDisplay {
  name: string;
  meta: string[];
  status: ProjectStatus;
}

interface ProgressDisplay {
  label: string;
  value: number;
}

interface MetricItem {
  label: string;
  value: string;
  variant?: string;
  sub?: string;
  size?: string;
  subVariant?: string;
}

interface ProgressItem {
  label: string;
  value: string;
  isFlagged?: boolean;
  flagNote?: string;
}

interface ProgressPhaseItem {
  id: string;
  label: string;
  value: string;
  isFlagged: boolean;
  notes: string[];
  flagNote?: string;
}

interface ProgressPhaseData {
  key: string;
  label: string;
  weight: number;
  estimation: number;
  items: ProgressPhaseItem[];
}

interface Contact {
  initials: string;
  name: string;
  role: string;
  avatarVariant: string;
  action: string;
  secondText?: string;
}

interface ActivityItem {
  icon: string;
  title: string;
  meta: string;
}

interface ChecklistDoc {
  id?: string;
  status: string;
  icon: string;
  label: string;
  sub: string;
  action?: boolean;
}

interface Milestone {
  label: string;
  state: string;
  icon: string;
}

interface TableRow {
  label: string;
  value: string;
  variant?: string;
  size?: string;
  isTotal?: boolean;
}

interface ScheduleProgress {
  timeValue: string;
  timeClass: string;
  workValue: string;
  workClass: string;
}

interface CompletionInfo {
  label: string;
  value: number;
}

interface SidebarBadge {
  text: string;
  variant: string;
}

interface SidebarSubItem {
  id: string;
  label: string;
  badge?: SidebarBadge;
}

interface SidebarItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  badge?: SidebarBadge;
  subItems?: SidebarSubItem[];
}

interface SidebarSection {
  label: string;
  items: SidebarItem[];
}

// ── Constants ─────────────────────────────────────────────────────────────────

const CO_STATUS_MAP: Record<string, { label: string; variant: string }> = {
  [CO_STATUS.PENDING]: { label: "⏳ Pending Owner", variant: "pending" },
  [CO_STATUS.APPROVED]: { label: "✓ Approved", variant: "active" },
  [CO_STATUS.REJECTED]: { label: "✗ Rejected", variant: "rejected" },
  bank_change_request: { label: "Change Requested", variant: "pending" },
  [CO_STATUS.CHANGE_REQUESTED]: {
    label: "↩ Info Requested",
    variant: "pending",
  },
  draft: { label: "Draft", variant: "pending" },
  [CO_STATUS.SYSTEM_APPROVED]: { label: "✓ Auto Approved", variant: "active" },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtCoDate(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// The backend renamed the "approved" status value when Change Order approval
// moved from Lender to Owner. Normalize any change order still carrying the
// old value so every status comparison here only has to know one value.
function normalizeCoStatus(co: RawChangeOrder): RawChangeOrder {
  const status = normalizeChangeOrderStatus(co.status);
  return status === co.status ? co : { ...co, status };
}

function mapCoRow(co: RawChangeOrder): ChangeOrderRow {
  const s = CO_STATUS_MAP[co.status ?? ""] ?? {
    label: co.status ?? "Draft",
    variant: "pending",
  };
  const amt = co.totalAmount?.amount ?? 0;
  const sym = co.totalAmount?.currency?.symbol ?? "$";
  const amtStr =
    amt >= 0
      ? `+${sym}${Math.abs(amt).toLocaleString("en-US")}`
      : `-${sym}${Math.abs(amt).toLocaleString("en-US")}`;
  const impact = co.scheduleImpact;
  const schedule =
    impact == null || impact === "" || impact === 0
      ? "None"
      : typeof impact === "string"
        ? impact
        : `${impact > 0 ? "+" : ""}${impact} day${Math.abs(impact) !== 1 ? "s" : ""}`;
  return {
    id: co.number ?? co.coNumber ?? co.id ?? "—",
    description: co.title ?? "—",
    submitted: fmtCoDate(co.publishedAt ?? co.submittedAt ?? co.createdAt),
    amount: amtStr,
    amountClass: amt >= 0 ? "coAmtPos" : "coAmtNeg",
    schedule,
    status: s.label,
    statusVariant: s.variant,
    isPrimary: co.status === CO_STATUS.PENDING,
  };
}

function buildCoSummary(addOns?: ChangeOrderAddOns): CoSummaryDisplay {
  const sym = addOns?.approvedCoSum?.amount?.currency?.symbol ?? "$";
  const approvedAmt = addOns?.approvedCoSum?.amount?.amount;
  const revisedAmt = addOns?.revisedContractValue?.amount;
  const pendingAmt = addOns?.pendingCoSum?.amount?.amount;
  const fmt = (n?: number): string =>
    n != null ? `${sym}${Math.abs(n).toLocaleString("en-US")}` : "—";
  return {
    approved: approvedAmt != null ? `+${fmt(approvedAmt)}` : "—",
    pending: pendingAmt != null ? `+${fmt(pendingAmt)}` : "—",
    revised: fmt(revisedAmt),
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

const GcProjectDetailPage: React.FC = () => {
  const { projectId, activeTab } = useParams<{
    projectId: string;
    activeTab?: string;
  }>();
  const navigate = useNavigate();
  const { setCrumbs } = useGcLayout();
  const [activeSection, setActiveSection] = useState<string>(
    activeTab ?? "overview",
  );
  const showToast = useToast();
  const [projectData, setProjectData] = useState<ProjectData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [sovVersions, setSovVersions] = useState<SovVersion[]>([]);
  const [sovVersionsLoaded, setSovVersionsLoaded] = useState(false);
  const [lienItemsData, setLienItemsData] = useState<LienItemsData>({});
  const [sendingAll, setSendingAll] = useState<boolean>(false);
  const [sendingIds, setSendingIds] = useState<Set<string>>(new Set());
  const [changeOrders, setChangeOrders] = useState<ChangeOrderRow[]>([]);
  const [coSummaryData, setCoSummaryData] = useState<CoSummaryDisplay | null>(
    null,
  );
  const [liveProjectedContract, setLiveProjectedContract] = useState<
    number | null
  >(null);
  const [coLoading, setCoLoading] = useState<boolean>(true);
  const [lienLoading, setLienLoading] = useState<boolean>(true);
  const [overviewData, setOverviewData] = useState<OverviewApiData | null>(
    null,
  );
  const [overviewLoading, setOverviewLoading] = useState<boolean>(true);
  const coFetchedRef = React.useRef(false);
  const lienFetchedRef = React.useRef(false);
  const overviewFetchedRef = React.useRef(false);
  const preconFetchedRef = React.useRef(false);
  const [preconReview, setPreconReview] = React.useState<any>(null);
  const [preconLoading, setPreconLoading] = React.useState(false);
  const [preconNotFound, setPreconNotFound] = React.useState(false);

  const budgetFetchedRef = React.useRef(false);
  const [budgetData, setBudgetData] = React.useState<any>(null);
  const [budgetLoading, setBudgetLoading] = React.useState(false);

  const lenderPreconFetchedRef = React.useRef(false);
  const [lenderPreconData, setLenderPreconData] = React.useState<any>(null);
  const [lenderPreconLoading, setLenderPreconLoading] = React.useState(false);
  const [lenderPreconNotFound, setLenderPreconNotFound] = React.useState(false);

  useEffect(() => {
    const fetchProjectData = async (): Promise<void> => {
      try {
        setLoading(true);
        const response = await getProject(projectId!);
        setProjectData(response.data?.data ?? null);
        setError(null);
      } catch (err) {
        console.error("Error fetching project:", err);
        setError("Failed to load project details");
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      fetchProjectData();
    }
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    getSovVersions(projectId).then(
      ({ data }: { data: { data?: SovVersion[] } | null }) => {
        if (data && Array.isArray(data.data)) {
          setSovVersions(
            [...data.data].sort((a, b) => b.versionNumber - a.versionNumber),
          );
        }
        setSovVersionsLoaded(true);
      },
    );
  }, [projectId]);

  useEffect(() => {
    if (activeSection !== "sov-version" || sovVersions.length === 0) return;
    const firstId = sovVersions[0].id;
    setActiveSection(`sov-version-${firstId}`);
    navigate(`/gc-project-detail/${projectId}/sov-version-${firstId}`, {
      replace: true,
    });
  }, [activeSection, sovVersions]);

  useEffect(() => {
    if (activeSection !== "change-orders" || !projectId || coFetchedRef.current)
      return;
    coFetchedRef.current = true;
    setCoLoading(true);
    getGcChangeOrders(projectId).then((result: any) => {
      const data = result.data as {
        data?: RawChangeOrder[];
        addOns?: ChangeOrderAddOns;
      } | null;
      const fetchError = result.error as { message?: string } | null;
      if (fetchError) {
        showToast(fetchError.message ?? "Failed to load change orders.");
      } else {
        setChangeOrders(
          (data?.data ?? []).map(normalizeCoStatus).map(mapCoRow),
        );
        setCoSummaryData(buildCoSummary(data?.addOns));
      }
      setCoLoading(false);
    });
  }, [projectId, activeSection]);

  useEffect(() => {
    if (activeSection !== "waivers" || !projectId || lienFetchedRef.current)
      return;
    lienFetchedRef.current = true;
    setLienLoading(true);
    getGcLienItems(projectId).then(
      ({ data }: { data: { data?: LienItemsData } | null }) => {
        if (data?.data) setLienItemsData(data.data);
        setLienLoading(false);
      },
    );
  }, [projectId, activeSection]);

  useEffect(() => {
    if (!projectId || overviewFetchedRef.current) return;
    overviewFetchedRef.current = true;
    setOverviewLoading(true);
    getGcProjectOverview(projectId).then((result: any) => {
      const d = result?.data?.data as OverviewApiData | null;
      if (d) setOverviewData(d);
      setOverviewLoading(false);
    });
  }, [projectId]);

  useEffect(() => {
    if (
      activeSection !== "precon-checklist" ||
      !projectId ||
      preconFetchedRef.current
    )
      return;
    preconFetchedRef.current = true;
    setPreconLoading(true);
    (getGcPreconReview(projectId) as Promise<any>).then((result: any) => {
      if (result?.data?.data) setPreconReview(result.data.data);
      if (result?.error?.status === 404) setPreconNotFound(true);
      setPreconLoading(false);
    });
  }, [projectId, activeSection]);

  const handlePreconDocUpload = async (
    itemId: string,
    file: File,
  ): Promise<void> => {
    try {
      const { key } = await uploadFileToS3(file);
      const { error } = (await uploadChecklistDocument(projectId!, itemId, {
        key,
        fileName: file.name,
      })) as any;
      if (error) {
        showToast("Upload failed. Please try again.");
        return;
      }
      showToast("Document uploaded successfully");
      preconFetchedRef.current = false;
      setPreconLoading(true);
      (getGcPreconReview(projectId!) as Promise<any>).then((result: any) => {
        if (result?.data?.data) setPreconReview(result.data.data);
        setPreconLoading(false);
      });
    } catch {
      showToast("Upload failed. Please try again.");
    }
  };

  useEffect(() => {
    if (
      activeSection !== "precon" ||
      !projectId ||
      lenderPreconFetchedRef.current
    )
      return;
    lenderPreconFetchedRef.current = true;
    setLenderPreconLoading(true);
    (getLenderPreconReview(projectId, true) as Promise<any>).then(
      (res: any) => {
        if (!res.error && res.data?.data) setLenderPreconData(res.data.data);
        if (res.error?.status === 404) setLenderPreconNotFound(true);
        setLenderPreconLoading(false);
      },
    );
  }, [projectId, activeSection]);

  useEffect(() => {
    if (activeSection !== "budget" || !projectId || budgetFetchedRef.current)
      return;
    budgetFetchedRef.current = true;
    setBudgetLoading(true);
    (getGcScheduleBudget(projectId) as Promise<any>).then((result: any) => {
      if (result?.data?.data) setBudgetData(result.data.data);
      setBudgetLoading(false);
    });
  }, [projectId, activeSection]);

  const project: ProjectDisplay = projectData
    ? {
        name: projectData.projectName || "",
        meta: [
          projectData.projectAddress ? `📍 ${projectData.projectAddress}` : "",
          projectData.lender
            ? `🏦 ${projectData.lender?.companyName}${projectData.lender.firstName || projectData.lender.lastName ? ` · ${projectData.lender.firstName ?? projectData.lender.firstName} ${projectData.lender.lastName ?? projectData.lender.lastName}` : ""}`
            : "",
          projectData.projectType
            ? `🏗 ${capitalizeFirst(projectData.projectType)}`
            : "",
          projectData.contractValue
            ? `📋 Contract: ${projectData.contractValue?.currency?.symbol}${projectData.contractValue?.amount?.toLocaleString()}`
            : "",
        ].filter(Boolean) as string[],
        status: {
          label:
            STATUS_LABELS[overviewData?.status || ""] ??
            formatStatus(overviewData?.status),
          variant:
            overviewData?.status === "draw_request" ? "pending" : "active",
        },
      }
    : {
        name: "Loading...",
        meta: [],
        status: { label: "Loading", variant: "pending" },
      };

  const progress: ProgressDisplay = projectData
    ? {
        label: `${projectData.progressPercentage ? Math.round(projectData.progressPercentage) : 0}%`,
        value: projectData.progressPercentage
          ? Math.round(projectData.progressPercentage)
          : 0,
      }
    : { label: "0%", value: 0 };

  const fmtOverviewAmt = (field?: OverviewAmountField): string => {
    if (field?.amount == null) return "—";
    const sym = field.currency?.symbol ?? "$";
    return `${sym}${field.amount.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  };

  const fmtEstMonth = (iso?: string): string => {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d.getTime())
      ? iso
      : d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  };

  const mkInitials = (p?: OverviewStakeholderPerson): string =>
    ((p?.firstName?.[0] ?? "") + (p?.lastName?.[0] ?? "")).toUpperCase();

  const mkName = (p?: OverviewStakeholderPerson): string =>
    [p?.firstName, p?.lastName].filter(Boolean).join(" ");

  const metrics: MetricItem[] = overviewData
    ? [
        {
          label: "Contract Value",
          value: fmtOverviewAmt(
            overviewData.updatedContractValue ?? overviewData.contractAmount,
          ),
          variant: "blue",
          sub: "Incl. approved COs",
        },
        {
          label: "Billed to Date",
          value: fmtOverviewAmt(overviewData.billedToDate),
          sub:
            overviewData.drawRequest?.drawNumber != null
              ? `Through Draw #${String(overviewData.drawRequest.drawNumber).padStart(2, "0")}`
              : "",
        },
        {
          label: "Balance to Bill",
          value: fmtOverviewAmt(overviewData.balanceToBill),
          variant: "green",
          sub: "Remaining on contract",
        },
        {
          label: "Est. Completion",
          value: fmtEstMonth(overviewData.estCompletionDate),
          variant: "amber",
          size: "small",
          sub: (() => {
            if (!overviewData.estCompletionDate) return undefined;
            const est = new Date(overviewData.estCompletionDate);
            const diffDays = Math.round(
              (est.getTime() - Date.now()) / (1000 * 60 * 60 * 24),
            );
            if (diffDays < 0)
              return `~${Math.abs(Math.round(diffDays / 7))} wk${Math.abs(Math.round(diffDays / 7)) !== 1 ? "s" : ""} overdue`;
            const weeks = Math.round(diffDays / 7);
            return weeks === 0
              ? "This week"
              : `~${weeks} wk${weeks !== 1 ? "s" : ""} remaining`;
          })(),
          subVariant: (() => {
            if (!overviewData.estCompletionDate) return undefined;
            const diffDays = Math.round(
              (new Date(overviewData.estCompletionDate).getTime() -
                Date.now()) /
                (1000 * 60 * 60 * 24),
            );
            return diffDays < 0 ? "red" : diffDays < 30 ? "amber" : undefined;
          })(),
        },
      ]
    : [];

  const snapshot = overviewData?.progressReport?.inspectionSnapshot;
  const lineItems = snapshot?.lineItems ?? [];
  const phases = snapshot?.phases ?? [];

  const progressItems: ProgressItem[] = lineItems.map((item) => {
    const pct = Math.min(100, Math.round((item.gcBilledPct ?? 0) * 100));
    return {
      label: item.aiaItem?.itemName ?? "—",
      value: `${pct}`,
      isFlagged: item.isFlagged ?? false,
      flagNote:
        item.isFlagged && item.isIncludedFlagInReport
          ? item.note?.flag
          : undefined,
    };
  });

  const progressPhases: ProgressPhaseData[] = phases.map((phase) => ({
    key: phase.phase,
    label: `Phase ${phase.phase}`,
    weight: phase.phaseWeight,
    estimation: phase.phaseCscEstimation,
    items: phase.items.map((item) => ({
      id: item.id,
      label: item.itemLabel,
      value: `${item.cscEstimation}`,
      isFlagged: item.isFlagged,
      notes: item.note
        ? Object.entries(item.note)
            .filter(([k]) => k !== "flag" && !isNaN(Number(k)))
            .map(([, v]) => v)
        : [],
      flagNote: item.note?.flag,
    })),
  }));

  const allFlaggedItems =
    phases.length > 0
      ? phases
          .flatMap((p) => p.items)
          .filter((i) => i.isFlagged && i.isIncludedFlagInReport)
          .map((i) => {
            const comment = i.note?.flag ?? "";
            return `⚠ CSC flagged "${i.itemLabel}"${comment ? ` — ${comment}` : ""}.`;
          })
      : lineItems
          .filter((i) => i.isFlagged && i.isIncludedFlagInReport)
          .map((i) => {
            const name = i.aiaItem?.itemName ?? "an item";
            const comment = i.note?.flag ?? "";
            return `⚠ CSC flagged "${name}"${comment ? ` — ${comment}` : ""}.`;
          });

  const progressNote = allFlaggedItems.length > 0 ? allFlaggedItems : undefined;

  const {
    owner,
    lender: ovLender,
    inspectors,
    architect,
  } = overviewData?.stakeholders ?? {};

  const contacts: Contact[] = [
    ...(inspectors?.[0]
      ? [
          {
            initials: mkInitials(inspectors[0]),
            name: mkName(inspectors[0]),
            role: inspectors[0].companyName ?? "Inspector",
            avatarVariant: "blue",
            action: "message",
            secondText: inspectors[0].phone,
          },
        ]
      : []),
    ...(ovLender
      ? [
          {
            initials: mkInitials(ovLender),
            name: mkName(ovLender),
            role: ovLender.companyName ?? "",
            avatarVariant: "amber",
            action: "message",
            secondText: "Lending Officer",
          },
        ]
      : []),
    ...(owner
      ? [
          {
            initials: mkInitials(owner),
            name: mkName(owner),
            role: "Owner",
            avatarVariant: "green",
            action: "message",
            secondText: owner.companyName ?? "",
          },
        ]
      : []),
    ...(architect
      ? [
          {
            initials: mkInitials(architect),
            name: mkName(architect),
            role: `Architect · ${architect.companyName ?? ""}`,
            avatarVariant: "gray",
            action: architect.email ?? "",
            secondText: architect.address,
          },
        ]
      : []),
  ];

  const ACTIVITY_ICON: Record<string, string> = {
    PRECON_SUBMITTED: "📄",
    PROJECT_PUBLISHED: "📋",
    CHECKLIST_PUBLISHED: "✅",
    GC_REQUIREMENTS_PUBLISHED: "✅",
    PROJECT_CREATED: "🏗",
    DRAW_SUBMITTED: "📤",
    DRAW_APPROVED: "✅",
    DRAW_REJECTED: "❌",
    INSPECTION_COMPLETED: "🔍",
    CHANGE_ORDER_SUBMITTED: "📝",
    CHANGE_ORDER_APPROVED: "✅",
  };

  const recentActivity: ActivityItem[] = (
    overviewData?.recentActivities?.activities ?? []
  ).map((a) => ({
    icon: ACTIVITY_ICON[a.type] ?? "📌",
    title: a.title,
    meta: [formatDate(a.date, false), a.performedBy]
      .filter(Boolean)
      .join(" · "),
  }));

  const fmtAmount = (field: CurrencyAmount): string =>
    `${field.currency.symbol}${field.amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const fmtDate = (iso?: string): string =>
    iso
      ? new Date(iso).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "—";

  const drawNumbers = Object.keys(lienItemsData).sort(
    (a, b) => Number(b) - Number(a),
  );
  const currentDrawNum = drawNumbers[0];
  const priorDrawNums = drawNumbers.slice(1);

  const currentWaivers: WaiverRow[] = currentDrawNum
    ? lienItemsData[currentDrawNum].lienItems.map((item) => {
        const signed = !!item.lienWaiver?.signedAt;
        const invited = (item.lienWaiver?.inviteCount ?? 0) > 0;
        return {
          vendor: item.subContractorName,
          amount: fmtAmount(item.totalAmount),
          sent: fmtDate(item.lienWaiver?.invitedAt),
          status: signed
            ? `✓ Signed — ${fmtDate(item.lienWaiver?.signedAt)}`
            : invited
              ? "⏳ Sent — awaiting signature"
              : "Not yet sent",
          statusClass: signed
            ? "lwStatusSigned"
            : invited
              ? "lwStatusPending"
              : "lwStatusNotSent",
          drawId: item?.id,
          lienWaiverId: item.lienWaiver?.id,
          sending: !!item.lienWaiver?.id && sendingIds.has(item.lienWaiver.id),
          action: !signed
            ? {
                label: invited ? "Remind" : "Send Waiver",
                variant: invited ? "remind" : "",
              }
            : undefined,
        };
      })
    : [];

  const priorWaivers: WaiverRow[] = priorDrawNums.map((drawNum) => {
    const items = lienItemsData[drawNum].lienItems;
    const total = items.reduce((sum, i) => sum + i.totalAmount.amount, 0);
    const symbol = items[0]?.totalAmount?.currency?.symbol ?? "$";
    return {
      vendor: `Draw #${drawNum}`,
      amount: `${items.length} vendor${items.length !== 1 ? "s" : ""}`,
      sent: `${symbol}${total.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      status: "✓ All waivers received",
      statusClass: "lwStatusSigned",
    };
  });

  const onSendAllWaivers = async (): Promise<void> => {
    if (!projectId) return;
    const pendingRows = currentWaivers.filter(
      (row): row is WaiverRow & { drawId: string; lienWaiverId: string } =>
        !!(row.action && row.drawId && row.lienWaiverId),
    );
    if (!pendingRows.length) return;
    setSendingAll(true);
    const results = await Promise.all(
      pendingRows.map((row) =>
        inviteLienWaiver(projectId, row.drawId, row.lienWaiverId),
      ),
    );
    setSendingAll(false);
    const failed = results.some((r: { error: unknown }) => r.error);
    if (failed) {
      showToast("Failed to send some waivers. Please try again.");
    } else {
      showToast("All reminders sent successfully.");
      getGcLienItems(projectId).then(
        ({ data }: { data: { data?: LienItemsData } | null }) => {
          if (data?.data) setLienItemsData(data.data);
        },
      );
    }
  };

  const onSendWaiver = async (row: WaiverRow): Promise<void> => {
    if (!row?.drawId || !row?.lienWaiverId || !projectId) return;
    const { drawId, lienWaiverId } = row;
    setSendingIds((prev) => new Set([...prev, lienWaiverId]));
    const { error: sendError } = await inviteLienWaiver(
      projectId,
      drawId,
      lienWaiverId,
    );
    setSendingIds((prev) => {
      const next = new Set(prev);
      next.delete(lienWaiverId);
      return next;
    });
    if (sendError) {
      showToast(
        (sendError as { message?: string }).message || "Failed to send waiver",
      );
    } else {
      showToast(`Waiver sent to ${row.vendor}`);
      getGcLienItems(projectId).then(
        ({ data }: { data: { data?: LienItemsData } | null }) => {
          if (data?.data) setLienItemsData(data.data);
        },
      );
    }
  };

  const changeSummary: ChangeSummaryProps = {
    ...(coSummaryData ?? { approved: "—", pending: "—", revised: "—" }),
    // While creating the first change order (none exist yet), show the wizard's
    // live projected contract instead of the API's revised-contract figure
    ...(changeOrders.length === 0 && liveProjectedContract != null
      ? {
          revised: `$${Math.abs(liveProjectedContract).toLocaleString("en-US")}`,
        }
      : {}),
    onSubmit: () => showToast("Opening change order..."),
  };
  const fmtKey = (key: string) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const fmtQuestionValue = (value: unknown): string => {
    if (value == null) return "—";
    if (typeof value === "string" || typeof value === "number")
      return String(value);
    if (typeof value === "object") {
      const v = value as Record<string, unknown>;
      if (v.amount != null) return `$${v.amount}`;
      return Object.entries(v)
        .filter(([, val]) => val != null && val !== "")
        .map(([k, val]) => `${fmtKey(k)}: ${val}`)
        .join(", ");
    }
    return "—";
  };

  const { docLabels, qaQuestions } = getPreconConfig(
    projectData?.projectType ?? "commercial",
  );

  const answered = preconReview?.answered ?? 0;
  const totalItemCount = preconReview?.totalItemCount ?? 0;
  const completionPct =
    totalItemCount > 0 ? Math.round((answered / totalItemCount) * 100) : 0;
  const completion: CompletionInfo = {
    label: preconReview ? `${answered} of ${totalItemCount}` : "—",
    value: completionPct,
  };

  const preconDocs: ChecklistDoc[] = preconReview
    ? preconReview.documents.map((doc: any) => {
        const cfg = docLabels.find((d) => d.key === doc.key);
        const label = cfg
          ? cfg.label + (cfg.extra ? ` ${cfg.extra}` : "")
          : fmtKey(doc.key);
        return {
          id: doc.id,
          status: doc.value ? "done" : "missing",
          icon: doc.value ? "✓" : "!",
          label,
          sub: doc.value
            ? `Uploaded · ${formatDate(doc?.publishedAt ?? doc?.uploadedAt, false)}`
            : "Required — not yet uploaded",
          action: !doc.value,
        };
      })
    : [];

  const qaItems: ChecklistDoc[] = preconReview
    ? preconReview.questions.map((q: any) => {
        const cfg = qaQuestions.find((qc) => qc.key === q.key);
        return {
          status: q.value != null ? "done" : "missing",
          icon: q.value != null ? "✓" : "!",
          label: cfg ? cfg.label : fmtKey(q.key),
          sub:
            q.value != null
              ? `Response: ${fmtQuestionValue(q.value)}`
              : "Not answered",
        };
      })
    : [];

  const milestones: Milestone[] = [
    { label: "Requirements Acknowledged", state: "done", icon: "✓" },
    { label: "Pre-Con Checklist Submitted", state: "done", icon: "✓" },
    { label: "Pre-Con Report Approved", state: "done", icon: "✓" },
    { label: "Construction In Progress", state: "active", icon: "🔍" },
    { label: "Substantial Completion", state: "", icon: "○" },
    { label: "Final Draw & Close-Out", state: "", icon: "○" },
  ];

  const fmtMoney = (field?: {
    amount?: number;
    currency?: { symbol?: string };
  }) => {
    if (!field) return "—";
    const sym = field.currency?.symbol ?? "$";
    const amt = field.amount ?? 0;
    return `${sym}${amt.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const fmtDateShort = (iso?: string) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
  };

  const g702 = budgetData?.g702;
  const sched = budgetData?.scheduleSummary;
  const cos: any[] = budgetData?.changeOrders ?? [];

  const coStatusVariant = (s: string) =>
    isCoApproved(s) ? "green" : s === CO_STATUS.REJECTED ? "red" : "amber";

  const contractRows: TableRow[] = g702
    ? [
        {
          label: "Original Contract Sum",
          value: fmtMoney(g702.originalContractSum),
        },
        { label: "SOV Update", value: fmtMoney(g702.sovUpdate) },
        ...cos.map((co) => ({
          label: `${co.coNumber} —  ${formatStatus(co.status)}`,
          value: `+${fmtMoney(co.amount)}`,
          variant: coStatusVariant(co.status),
        })),
        {
          label: "Revised Contract (approved COs)",
          value: fmtMoney(g702.contractSumToDate),
          variant: "blue",
          size: "large",
          isTotal: true,
        },
        {
          label: "Total Billed to Date",
          value: fmtMoney(g702.totalCompletedAndStored),
        },
        {
          label: `Retainage Held (${g702.retainageRate ?? ""}%)`,
          value: `-${fmtMoney(g702.totalRetainage)}`,
          variant: "red",
        },
        {
          label: "Balance to Finish",
          value: fmtMoney(g702.balanceToFinish),
          variant: "green",
          size: "large",
          isTotal: true,
        },
      ]
    : [];

  const scheduleImpacts: TableRow[] =
    sched?.scheduleImpact?.breakdown?.map((b: any) => ({
      label: `Schedule Impact (${b.coNumber})`,
      value: `+${b.extendedDays} day${b.extendedDays !== 1 ? "s" : ""}`,
      variant: "amber",
    })) ?? [];

  const scheduleRows: TableRow[] = sched
    ? [
        {
          label: "Mobilization Date",
          value: fmtDateShort(sched.mobilizationDate),
        },
        {
          label: "Original Completion",
          value: fmtDateShort(sched.originalCompletion),
        },
        ...scheduleImpacts,
        {
          label: "Revised Completion",
          value: fmtDateShort(sched.revisedCompletion),
        },
        ...(sched.currentAssessment
          ? [
              {
                label: `Report ${sched.currentAssessment.reportNumber} Assessment`,
                value: fmtDateShort(sched.currentAssessment.reportDate),
                variant: "amber" as const,
                size: "medium",
                isTotal: true,
              },
            ]
          : []),
      ]
    : [];

  const scheduleNote =
    sched?.currentAssessment?.scheduleNotes
      ?.replace(/^[•·\-\s]+/gm, "")
      .trim() ?? "";

  const workPct = Math.round(parseFloat(sched?.workComplete ?? "0"));
  const scheduleProgress: ScheduleProgress = {
    timeValue: `${sched?.timeElapsed ?? 0}%`,
    timeClass: `w${Math.min(100, sched?.timeElapsed ?? 0)}`,
    workValue: `${workPct}%`,
    workClass: `w${Math.min(100, workPct)}`,
  };

  const sidebarSections = useMemo<SidebarSection[]>(
    () => [
      {
        label: "Project",
        items: [
          { id: "overview", label: "Overview", icon: <OverviewIcon /> },
          {
            id: "update-progress",
            label: "Update Progress",
            icon: <UpdateProgressIcon />,
          },
          { id: "draws", label: "Draw Requests", icon: <DrawRequestIcon /> },
          { id: "waivers", label: "Lien Waivers", icon: <LineWaiverIcon /> },
          {
            id: "change-orders",
            label: "Change Orders",
            icon: <ChangeOrderIcon />,
          },
        ],
      },
      {
        label: "Setup",
        items: [
          {
            id: "precon-checklist",
            label: "Pre-Con Checklist",
            icon: <PreconChecklistIcon />,
          },
          {
            id: "precon",
            label: "Pre-Construction",
            icon: <PreConstructionReportIcon />,
          },
        ],
      },
      {
        label: "Finance",
        items: [
          {
            id: "aia-billing",
            label: "AIA Billing Summary",
            icon: <AiaBillingIcon />,
          },
          { id: "budget", label: "Schedule & Budget", icon: <BudgetIcon /> },
        ],
      },
      {
        label: "Project Files",
        items: [
          { id: "documents", label: "Documents", icon: <DocIcon /> },
          { id: "messages", label: "Messages", icon: <MessageIcon /> },
        ],
      },
      {
        label: "SOV Setup",
        items: [
          {
            id: "sov-version",
            label: "Schedule of Values",
            icon: <ScheduleOfValuesIcon />,
            subItems: sovVersions.map((v) => ({
              id: `sov-version-${v.id}`,
              versionNumber: v.versionNumber,
              label: `Version ${v.versionNumber}`,
              badge: {
                text: v.status === "superseded" ? "Inactive" : v.status,
                variant: v.status === "active" ? "view" : "",
              },
            })),
          },
        ],
      },
    ],
    [sovVersions],
  );

  useEffect(() => {
    let sectionLabel = "Overview";
    outer: for (const section of sidebarSections) {
      for (const item of section.items) {
        if (item.id === activeSection) {
          sectionLabel = item.label;
          break outer;
        }
        const sub = item.subItems?.find((s) => s.id === activeSection);
        if (sub) {
          sectionLabel = sub.label;
          break outer;
        }
      }
    }
    setCrumbs([
      { label: "My Projects", link: "/gc-dashboard" },
      { label: project.name, link: `/gc-project-detail/${projectId}` },
      { label: sectionLabel },
    ]);
  }, [project.name, projectId, activeSection, sidebarSections, setCrumbs]);

  const handleSelectSection = (id: string): void => {
    setActiveSection(id);
    navigate(`/gc-project-detail/${projectId}/${id}`, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return <GcProjectDetailSkeleton />;
  }

  if (error) {
    return <NetworkError />;
  }
  return (
    <div>
      <ProjectBanner project={project} progress={progress} />
      <div className="bodyWrap">
        <Sidebar
          sections={sidebarSections as any}
          activeSection={activeSection}
          onSelect={handleSelectSection}
        />
        <div className="main">
          <OverviewSection
            isActive={activeSection === "overview"}
            onShowSection={handleSelectSection}
            metrics={metrics}
            progressItems={progressItems}
            progressPhases={progressPhases}
            progressNote={progressNote}
            contacts={contacts}
            recentActivity={recentActivity}
            drawRequest={overviewData?.drawRequest}
            loading={overviewLoading}
            inspectionDate={overviewData?.progressReport?.inspectionDate}
          />
          <UpdateProgress
            isActive={activeSection === "update-progress"}
            onShowSection={handleSelectSection}
            projectId={projectId ?? ""}
            cscCompanyName={
              projectData?.cscCompanyName ??
              projectData?.adminCompanyName ??
              "CSC New England"
            }
            lenderCompanyName={
              projectData?.lender?.companyName ??
              projectData?.stakeholders?.lenderBranch?.companyName ??
              "the bank"
            }
          />
          <DrawsSection
            isActive={activeSection === "draws"}
            projectId={projectId ?? ""}
            lender={projectData?.lender}
            projectStatus={overviewData?.status ?? ""}
          />
          <WaiversSection
            isActive={activeSection === "waivers"}
            currentRows={currentWaivers}
            priorRows={priorWaivers}
            currentDrawNum={currentDrawNum}
            onSendWaiver={onSendWaiver}
            onSendAllWaivers={onSendAllWaivers}
            sendingAll={sendingAll}
            loading={lienLoading}
          />
          <ChangeOrdersSection
            isActive={activeSection === "change-orders"}
            // rows={changeOrders}
            summary={changeSummary}
            loading={coLoading}
            projectStage={projectData?.projectStage}
            onProjectedContractChange={setLiveProjectedContract}
          />
          <PreconSection
            isActive={activeSection === "precon-checklist"}
            completion={completion}
            documents={preconDocs}
            qaItems={qaItems}
            onDocUpload={handlePreconDocUpload}
            loading={preconLoading}
            notFound={preconNotFound}
          />
          {activeSection === "precon" && (
            <BankPreconSection
              data={lenderPreconData}
              loading={lenderPreconLoading}
              notFound={lenderPreconNotFound}
              projectId={projectId}
              preconReportId={lenderPreconData?.preconstructionReportId}
            />
          )}
          <AiaBillingSection
            isActive={activeSection === "aia-billing"}
            projectId={projectId ?? ""}
            projectName={project.name}
          />
          <BudgetSection
            isActive={activeSection === "budget"}
            milestones={milestones}
            contractRows={contractRows}
            scheduleRows={scheduleRows}
            scheduleNote={scheduleNote}
            progress={scheduleProgress}
            loading={budgetLoading}
          />
          <DocumentsSection isActive={activeSection === "documents"} />
          <ConversationPanel
            projectId={projectId ?? ""}
            projectName={project.name}
            isActive={activeSection === "messages"}
            lender={projectData?.lender}
          />
          {activeSection === "sov-version" &&
            sovVersionsLoaded &&
            sovVersions.length === 0 && (
              <div className="ds active">
                <EmptyState
                  icon={<NoDocuments />}
                  title="Schedule of Values Not Submitted"
                  subtitle="The GC has not yet submitted a Schedule of Values for this project."
                  noBorder
                />
              </div>
            )}
          <SOVSetup
            isActive={activeSection.startsWith("sov-version-")}
            versionId={
              (activeSection.startsWith("sov-version-")
                ? activeSection.replace("sov-version-", "")
                : undefined) as any
            }
            contractAmount={projectData?.contractValue as any}
            lender={projectData?.lender as any}
          />
        </div>
      </div>
    </div>
  );
};

export default GcProjectDetailPage;
